import { z } from 'zod';
import {
  CITIES,
  LANGUAGES,
  REGIONS,
  SPECIALIZATIONS,
  type City,
  type Language,
  type Specialization,
  type UserRegion,
} from '../domain/onboarding-vocabularies';

/**
 * Контракты онбординга — Block 0 Phase 2 (PRD §2.2 + ADR-0012).
 *
 * Первый non-catalog контракт в проекте. Используется UI и MSW в Phase 2,
 * региональным API — в Phase 9+. Любое изменение → новый ADR (P-011).
 *
 * Persist policy ADR-0012 §2 (информационная нота для consumer'ов):
 *   `name`, `phone`, `specialization` — ТОЛЬКО in-memory в Zustand store;
 *   `lang`, `city`, `region`, `consents.marketingAnalytics`, `jwt` — persisted.
 *   Контракт описывает API payload, не shape store-state.
 *
 * `.strict()` на каждой `z.object` — защита от accidental data leak / injection
 * (см. `docs/examples/zod-contract.ts` § «Request schema (P-007)»).
 */

// ── Vocabulary enums ─────────────────────────────────────────────────────
// Type-coercion `as unknown as [T, ...T[]]` — паттерн из `paint.ts:30-40`
// (конвертация `Object.freeze([...] as const)` в формат, ожидаемый `z.enum`).
// Типы `City`, `Region`, `Language`, `Specialization` — канонически в domain;
// здесь только Zod-проекции, без переобъявления types.

const cityValues = CITIES as unknown as [City, ...City[]];
export const CitySchema = z.enum(cityValues);

// `UserRegionSchema` (не `RegionSchema`) — collision с `decor-match.ts` который
// экспортирует image-region (LAB photo-pick). См. note в `onboarding-vocabularies.ts`.
const regionValues = REGIONS as unknown as [UserRegion, ...UserRegion[]];
export const UserRegionSchema = z.enum(regionValues);

const languageValues = LANGUAGES as unknown as [Language, ...Language[]];
export const LanguageSchema = z.enum(languageValues);

const specializationValues = SPECIALIZATIONS as unknown as [Specialization, ...Specialization[]];
export const SpecializationSchema = z.enum(specializationValues);

// ── Phone E.164 (ADR-0012 §5) ────────────────────────────────────────────
// `+7XXXXXXXXXX` (KZ/RU, 11 цифр после `+7`) или `+998XXXXXXXXX` (UZ, 12 цифр
// после `+998`). На сервер всегда уходит E.164 — UI-маска — клиентская
// концепция, в payload не попадает. Текстовое сообщение валидации Phase 2 —
// hardcoded ru (LinguiJS-локализация — отдельная фаза).
export const PhoneE164Regex = /^\+7\d{10}$|^\+998\d{9}$/;
export const PhoneE164Schema = z.string().regex(PhoneE164Regex, {
  message: 'Телефон должен быть в формате +7XXXXXXXXXX или +998XXXXXXXXX',
});
export type PhoneE164 = z.infer<typeof PhoneE164Schema>;

// ── Consents (P-017 обязательное + P-035 опциональное) ───────────────────
// `dataProcessing: literal(true)` — без явного `true` регистрация не
// валидируется (MSW handler возвращает 422). Это enforcement P-017 «без
// согласия регистрация не завершается» на уровне контракта, а не UX.
//
// `marketingAnalytics: boolean` — отдельный чекбокс (P-035), отделён от
// dataProcessing. Юзер может зарегистрироваться без него (consent → false).
//
// `.strict()` блокирует undocumented поля (например, `consents.partnerSharing`
// которое появится в Phase 9+ — потребует расширения схемы через ADR).
export const ConsentsSchema = z
  .object({
    dataProcessing: z.literal(true),
    marketingAnalytics: z.boolean(),
  })
  .strict();
export type Consents = z.infer<typeof ConsentsSchema>;

// ── Stage 1 — Guest profile (PRD §2.2 «При первом входе») ────────────────
// `/onboarding/lang-city` submit. Гостевая запись хранится только в Zustand
// + localStorage (НЕ отправляется на сервер в Phase 2). В Phase 9+ возможна
// миграция гостевого профиля на сервер при сохранении ценного действия.
export const GuestProfileSchema = z
  .object({
    lang: LanguageSchema,
    city: CitySchema,
  })
  .strict();
export type GuestProfile = z.infer<typeof GuestProfileSchema>;

// ── Stage 2 — Register payload (PRD §2.2 «При повторном входе») ──────────
// `POST /api/onboarding/register` body. `region` НЕ передаётся клиентом —
// сервер выводит из `city` через свою копию `CITY_REGION_MAP` (ADR-0012 §4).
//
// `name.trim().min(2).max(50)` — JS `.length` (UTF-16 code units), что достаточно
// для KZ/RU/UZ имён без emoji. Edge case с emoji → B-056 backlog (ADR-0012
// nuance #3 от Aibek pre-step approve).
//
// `.trim()` обязателен — без него `"   "` (3 пробела) проходит .min(2) валидацию,
// и в (будущий) Postgres `users.name` попадает whitespace-only ПДн запись.
// Subagent code-review Step 1 (ISSUE 1, high severity) — fixed inline.
export const OnboardingPayloadSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, { message: 'Имя слишком короткое' })
      .max(50, { message: 'Имя слишком длинное' }),
    city: CitySchema,
    specialization: SpecializationSchema,
    phone: PhoneE164Schema,
    lang: LanguageSchema,
    consents: ConsentsSchema,
  })
  .strict();
export type OnboardingPayload = z.infer<typeof OnboardingPayloadSchema>;

// ── Stage 2 response от `POST /api/onboarding/register` ──────────────────
// `userId` — UUID (P-010, не messenger external_id). `jwt` — mock в Phase 2
// (`'mock-jwt-mvp'`); replaced в Phase 9+ на real OTP backend (B-011).
// `region` — server-derived из payload.city (ADR-0012 §6).
export const RegisterResponseSchema = z
  .object({
    jwt: z.string().min(1),
    userId: z.string().uuid(),
    region: UserRegionSchema,
  })
  .strict();
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;
